// SPDX-License-Identifier: GPL-3.0-or-later
//
// Orchestrates the "import an existing CV" flow:
//   1. parseCvFile() extracts plain text from the uploaded file.
//   2. cleanProfileFromCv() asks the AI to rewrite the noisy extracted text
//      as a clean profile blob suitable for storing in profile.json.
//   3. synthesiseCvTemplateFromCv() (optional) asks the AI to generate a
//      Typst template that mirrors the uploaded CV's layout.
//
// Step 2 is cheap (text-in, text-out) and uses the quick model. Step 3 is
// more demanding — it picks structural choices the renderer has to honour —
// so it uses the in-depth model.

import { renderTemplate } from '../typst/render';
import {
  clearActiveTemplate,
  writeOriginalTemplate,
  writeTemplateMeta,
} from '../fs/templates';
import { complete } from './client';
import { stripFences } from './parse';
import {
  profileImportSystemPrompt,
  profileImportUserPrompt,
  templateImportSystemPrompt,
  templateImportUserPrompt,
} from './prompts/import';

import type { CvParseResult } from '../fs/cv-parse';

export interface CleanProfileResult {
  text: string;
  model: string | undefined;
  provider: string | undefined;
}

export async function cleanProfileFromCv(parsed: CvParseResult): Promise<CleanProfileResult> {
  const res = await complete({
    kind: 'quick',
    system: profileImportSystemPrompt,
    user: profileImportUserPrompt({ rawText: parsed.text, sourceKind: parsed.kind }),
    // CVs run long. 4k tokens covers a senior CV with room to spare; if the
    // input was huge the cleaned output still won't blow past this.
    maxTokens: 4096,
  });
  return { text: res.content.trim(), model: res.model, provider: res.provider };
}

export interface TemplateSynthesisResult {
  template: string;
  notes?: string;
  model: string | undefined;
  provider: string | undefined;
}

interface ParsedTemplate {
  template: string;
  notes?: string;
}

function parseTemplateResponse(raw: string): ParsedTemplate | { error: string } {
  const text = stripFences(raw);
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch (err) {
    return { error: `AI output was not valid JSON: ${err instanceof Error ? err.message : String(err)}` };
  }
  if (!value || typeof value !== 'object') return { error: 'AI output was not a JSON object.' };
  const o = value as Record<string, unknown>;
  if (typeof o['template'] !== 'string' || !o['template'].trim()) {
    return { error: 'AI output missing "template" (string).' };
  }
  const out: ParsedTemplate = { template: o['template'] };
  if (typeof o['notes'] === 'string' && o['notes']) out.notes = o['notes'];
  return out;
}

// Walk the template with empty data — mirrors template-adjust.ts's check.
// Catches unbalanced {{#x}}/{{/x}}, which is the most common way a synthesised
// template breaks. Typst syntax errors only surface on actual compile.
function validateMustacheBalance(template: string): { ok: true } | { ok: false; message: string } {
  try {
    renderTemplate(template, {});
    return { ok: true };
  } catch (err) {
    return { ok: false, message: err instanceof Error ? err.message : String(err) };
  }
}

export async function synthesiseCvTemplateFromCv(
  parsed: CvParseResult,
): Promise<TemplateSynthesisResult> {
  const res = await complete({
    kind: 'in-depth',
    system: templateImportSystemPrompt,
    user: templateImportUserPrompt({ rawText: parsed.text, sourceKind: parsed.kind }),
    maxTokens: 4096,
  });
  const parsedOut = parseTemplateResponse(res.content);
  if ('error' in parsedOut) throw new Error(parsedOut.error);
  const balance = validateMustacheBalance(parsedOut.template);
  if (!balance.ok) {
    throw new Error(`Synthesised template is structurally broken: ${balance.message}`);
  }
  return {
    template: parsedOut.template,
    ...(parsedOut.notes !== undefined ? { notes: parsedOut.notes } : {}),
    model: res.model,
    provider: res.provider,
  };
}

// Persist the synthesised template as the user's cv.typ override. Drop any
// stale cv.active.typ + meta so the next generation either uses the new
// template as-is (until template-adjust runs) or re-analyses cleanly.
export async function persistSynthesisedCvTemplate(
  root: FileSystemDirectoryHandle,
  result: TemplateSynthesisResult,
): Promise<void> {
  await writeOriginalTemplate(root, 'cv', result.template);
  await clearActiveTemplate(root, 'cv');
  // Seed meta so the Settings UI shows "using your template" with a note
  // about provenance until the next analyse pass overwrites it.
  await writeTemplateMeta(root, 'cv', {
    schemaVersion: 1,
    source: 'user',
    gaps: [],
    analyzedAt: new Date().toISOString(),
    notes:
      result.notes !== undefined
        ? `Synthesised from uploaded CV. ${result.notes}`
        : 'Synthesised from uploaded CV.',
  });
}
